// v4.4 — Détail d'un catalogue commerçant (/kiosque/catalogue/:catalogId)
// + deep link produit (/kiosque/product/:productId). Design AYA Kiosque.
import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MapPin, Phone, Star, BadgeCheck, ChevronRight, Newspaper, Share2 } from "lucide-react";
import { trackCatalog } from "@/lib/catalogTracking";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const fmtPrice = (n) => (typeof n === "number" ? `${n.toLocaleString("fr-FR")} FCFA` : "");

// v4.6 — Feuilletage du catalogue (comme un journal AYA) : couverture + pages de 4 produits.
function FlipViewer({ catalog, merchant, products, onClose, onProduct }) {
  const [page, setPage] = useState(0);
  const chunks = [];
  for (let i = 0; i < products.length; i += 4) chunks.push(products.slice(i, i + 4));
  const total = 1 + chunks.length;
  const prev = () => setPage((p) => Math.max(0, p - 1));
  const next = () => setPage((p) => Math.min(total - 1, p + 1));
  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="catalog-flip-viewer">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <div className="text-[12.5px] font-bold truncate">{merchant?.name} — {catalog.title}</div>
        <button onClick={onClose} data-testid="flip-close-btn" className="text-[13px] font-bold px-3 py-1.5 rounded-full bg-white/10">✕ Fermer</button>
      </div>
      <div className="flex-1 relative overflow-hidden">
        {page === 0 ? (
          <img src={catalog.coverImage} alt={catalog.title} className="absolute inset-0 w-full h-full object-contain" />
        ) : (
          <div className="absolute inset-0 p-4 overflow-y-auto">
            <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
              {chunks[page - 1].map((p) => (
                <button key={p.product_id} onClick={() => onProduct(p)} data-testid={`flip-product-${p.product_id}`} className="rounded-2xl bg-[#141416] border border-white/12 p-3 text-left">
                  <div className="aspect-square rounded-xl bg-white/[0.05] flex items-center justify-center text-4xl overflow-hidden mb-2">
                    {p.photos?.[0] ? <img src={p.photos[0]} alt="" loading="lazy" className="w-full h-full object-cover" /> : (p.emoji || "🛍️")}
                  </div>
                  <div className="text-[11.5px] font-semibold text-white leading-tight line-clamp-2">{p.name}</div>
                  <div className="text-[12.5px] font-extrabold text-amber-300 mt-1">{fmtPrice(p.price_fcfa)}</div>
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Zones tactiles de feuilletage */}
        <button onClick={prev} aria-label="Page précédente" className="absolute left-0 inset-y-0 w-1/4" data-testid="flip-prev-zone" />
        <button onClick={next} aria-label="Page suivante" className="absolute right-0 inset-y-0 w-1/4" data-testid="flip-next-zone" />
      </div>
      <div className="flex items-center justify-center gap-5 py-3 text-white">
        <button onClick={prev} disabled={page === 0} data-testid="flip-prev-btn" className="w-9 h-9 rounded-full bg-white/10 disabled:opacity-30 font-bold">‹</button>
        <span className="text-[12px] text-white/70" data-testid="flip-page-counter">{page === 0 ? "Couverture" : `Page ${page}`} · {page + 1}/{total}</span>
        <button onClick={next} disabled={page === total - 1} data-testid="flip-next-btn" className="w-9 h-9 rounded-full bg-white/10 disabled:opacity-30 font-bold">›</button>
      </div>
    </div>
  );
}

function ProductSheet({ product, merchant, onClose }) {
  if (!product) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center" data-testid="product-sheet">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-[#111116] border-t sm:border border-white/12 rounded-t-3xl sm:rounded-3xl p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-16 h-16 rounded-2xl bg-white/[0.06] flex items-center justify-center text-3xl overflow-hidden shrink-0">
            {product.photos?.[0] ? <img src={product.photos[0]} alt="" className="w-full h-full object-cover" /> : (product.emoji || "🛍️")}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[15px] font-bold leading-tight">{product.name}</div>
            {product.brand && <div className="text-[11px] text-white/45">{product.brand}</div>}
            <div className="text-[15px] font-extrabold text-amber-300 mt-1">{fmtPrice(product.price_fcfa)}</div>
          </div>
        </div>
        <div className="text-[12px] text-white/60 flex items-center gap-1.5">
          {product.stock > 0 ? <span className="text-emerald-400 font-semibold">Disponible en magasin</span> : <span className="text-red-400">Rupture de stock</span>}
          {product.rating ? <span className="flex items-center gap-0.5 ml-2"><Star size={11} className="text-amber-300 fill-amber-300" /> {product.rating}</span> : null}
        </div>
        {merchant && (
          <div className="rounded-2xl bg-white/[0.05] border border-white/10 p-3 text-[12px] space-y-1">
            <div className="font-bold text-[13px] flex items-center gap-1.5">{merchant.name} {merchant.verified && <BadgeCheck size={14} className="text-amber-300" />}</div>
            <div className="text-white/55 flex items-center gap-1"><MapPin size={11} /> {merchant.address || merchant.location}</div>
            {merchant.phone && <div className="text-white/55 flex items-center gap-1"><Phone size={11} /> {merchant.phone}</div>}
          </div>
        )}
        <button onClick={onClose} className="w-full rounded-xl bg-amber-400 text-black font-bold text-[13.5px] py-3" data-testid="product-sheet-close">
          Retour au catalogue
        </button>
      </div>
    </div>
  );
}

export default function CatalogDetailPage({ productMode = false }) {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const src = searchParams.get("src") || "direct";
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [selected, setSelected] = useState(null);
  const [flipping, setFlipping] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (productMode) {
          // Deep link produit → résout le catalogue du commerçant puis surligne le produit
          const r = await axios.get(`${BACKEND_URL}/api/v3/catalogs/product/${params.productId}`);
          const { catalogId, product } = r.data;
          if (!catalogId) { setNotFound(true); return; }
          const rc = await axios.get(`${BACKEND_URL}/api/v3/catalogs/${catalogId}`);
          setData(rc.data);
          setSelected(product);
          trackCatalog("catalog_to_product", { catalog_id: catalogId, product_id: params.productId, src });
        } else {
          const rc = await axios.get(`${BACKEND_URL}/api/v3/catalogs/${params.catalogId}`);
          setData(rc.data);
          trackCatalog("catalog_view", { catalog_id: params.catalogId, merchant_id: rc.data?.catalog?.merchantId, src });
        }
      } catch (_) { setNotFound(true); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.catalogId, params.productId]);

  const share = async () => {
    const url = `${window.location.origin}/kiosque/catalogue/${data.catalog.catalogId}?src=whatsapp`;
    trackCatalog("catalog_share", { catalog_id: data.catalog.catalogId });
    try {
      if (navigator.share) await navigator.share({ title: data.catalog.title, url });
      else { await navigator.clipboard.writeText(url); toast.success("Lien du catalogue copié ✅"); }
    } catch (_) { /* partage annulé par l'utilisateur */ }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0a0a0d] text-white flex flex-col items-center justify-center gap-4 px-6">
        <div className="text-4xl">🏪</div>
        <div className="text-sm text-white/60 text-center">Ce catalogue n’est plus disponible.</div>
        <button onClick={() => navigate("/kiosque/catalogues")} className="rounded-xl bg-amber-400 text-black font-bold text-[13px] px-5 py-2.5">Voir tous les catalogues</button>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-[#0a0a0d] p-4"><div className="max-w-3xl mx-auto h-64 rounded-3xl bg-white/[0.05] animate-pulse mt-14" /></div>;
  }

  const { catalog, merchant, products, otherCatalogs } = data;
  const updated = catalog.updatedAt ? new Date(catalog.updatedAt) : null;
  const today = updated && new Date().toDateString() === updated.toDateString();

  return (
    <div className="min-h-screen bg-[#0a0a0d] text-white pb-16" data-testid="catalog-detail-page">
      <div className="sticky top-0 z-10 bg-[#0a0a0d]/95 backdrop-blur border-b border-white/8">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-3">
          <button onClick={() => navigate("/kiosque/catalogues")} className="p-1.5 -ml-1.5 rounded-full hover:bg-white/10 text-white/80" data-testid="catalog-back-btn">
            <ArrowLeft size={19} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-bold truncate">{merchant?.name}</div>
            <div className="text-[10.5px] text-white/45 capitalize">{merchant?.category} · {merchant?.location}</div>
          </div>
          <button onClick={share} className="p-2 rounded-full hover:bg-white/10 text-white/70" data-testid="catalog-share-btn"><Share2 size={17} /></button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-6">
        {/* Couverture + infos */}
        <div className="rounded-3xl overflow-hidden border border-white/10 bg-white/[0.04]">
          <div className="relative aspect-[16/10]">
            <img src={catalog.coverImage} alt={catalog.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0d] via-transparent to-transparent" />
            {catalog.isSponsored && <span className="absolute top-3 left-3 text-[10px] font-bold px-2.5 py-1 rounded-full bg-amber-400 text-black">Sponsorisé</span>}
          </div>
          <div className="p-4 -mt-8 relative">
            <h1 className="text-[19px] font-extrabold">{catalog.title}</h1>
            <div className="text-[12px] text-white/55 mt-0.5">{catalog.description}</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2.5 text-[11.5px] text-white/60">
              <span>📦 {catalog.productCount} produits</span>
              <span>🕐 {today ? "Mis à jour aujourd'hui" : `Maj ${updated ? updated.toLocaleDateString("fr-FR") : "—"}`}</span>
              {products.some((p) => p.stock > 0)
                ? <span className="text-emerald-400">Disponible en magasin</span>
                : products.length > 0 && <span className="text-red-400">Rupture de stock</span>}
            </div>
            {/* v4.6 — Feuilleter + partage WhatsApp */}
            <div className="flex gap-2 mt-3.5">
              <button
                onClick={() => { setFlipping(true); trackCatalog("catalog_open", { catalog_id: catalog.catalogId, src: "flip" }); }}
                data-testid="catalog-flip-btn"
                className="flex-1 rounded-xl bg-amber-400 text-black font-bold text-[12.5px] py-2.5"
              >
                📖 Feuilleter le catalogue
              </button>
              <button
                onClick={() => {
                  trackCatalog("catalog_share", { catalog_id: catalog.catalogId, src: "whatsapp" });
                  const url = `${window.location.origin}/kiosque/catalogue/${catalog.catalogId}?src=whatsapp`;
                  const msg = `🏪 *${merchant?.name}* — ${catalog.title}\n📦 ${catalog.productCount} produits · ${catalog.location}\nDécouvrez le catalogue sur AYA 👇\n${url}`;
                  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
                }}
                data-testid="catalog-whatsapp-btn"
                className="rounded-xl bg-[#25D366] text-black font-bold text-[12.5px] px-4 py-2.5 flex items-center gap-1.5"
              >
                <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </button>
            </div>
          </div>
        </div>

        {/* Produits */}
        <div>
          <div className="text-[12px] font-bold text-white/70 uppercase tracking-wider mb-2.5">Découvrir le catalogue</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {products.map((p) => (
              <button
                key={p.product_id}
                onClick={() => { setSelected(p); trackCatalog("catalog_product_click", { catalog_id: catalog.catalogId, product_id: p.product_id, src }); }}
                data-testid={`catalog-product-${p.product_id}`}
                className="text-left rounded-2xl bg-white/[0.04] border border-white/10 hover:border-amber-400/40 p-3 transition-colors"
              >
                <div className="aspect-square rounded-xl bg-white/[0.05] flex items-center justify-center text-4xl overflow-hidden mb-2 relative">
                  {p.photos?.[0] ? <img src={p.photos[0].startsWith("http") ? p.photos[0] : `${BACKEND_URL}${p.photos[0]}`} alt="" loading="lazy" className="w-full h-full object-cover" /> : (p.emoji || "🛍️")}
                  {!(p.stock > 0) && (
                    <span className="absolute bottom-1.5 left-1.5 text-[9px] font-extrabold px-2 py-0.5 rounded-md bg-black/80 text-red-400 border border-red-400/40" data-testid={`catalog-product-oos-${p.product_id}`}>Épuisé</span>
                  )}
                </div>
                <div className="text-[12px] font-semibold leading-tight line-clamp-2">{p.name}</div>
                <div className="text-[13px] font-extrabold text-amber-300 mt-1">{fmtPrice(p.price_fcfa)}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Autres catalogues du commerçant */}
        {otherCatalogs?.length > 0 && (
          <div>
            <div className="text-[12px] font-bold text-white/70 uppercase tracking-wider mb-2.5">Autres catalogues de {merchant?.name}</div>
            <div className="space-y-2">
              {otherCatalogs.map((c) => (
                <button key={c.catalogId} onClick={() => navigate(`/kiosque/catalogue/${c.catalogId}?src=${src}`)}
                  className="w-full flex items-center gap-3 rounded-2xl bg-white/[0.04] border border-white/10 hover:border-amber-400/40 px-3 py-2.5 text-left transition-colors">
                  <img src={c.coverImage} alt="" className="w-10 h-13 h-[52px] rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-bold truncate">{c.title}</div>
                    <div className="text-[10.5px] text-white/45">{c.productCount} produits</div>
                  </div>
                  <ChevronRight size={15} className="text-white/40" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Boucle commerce → contenu */}
        <button
          onClick={() => { trackCatalog("catalog_to_kiosque", { catalog_id: catalog.catalogId }); navigate("/kiosque"); }}
          data-testid="catalog-to-kiosque-btn"
          className="w-full rounded-2xl border border-white/12 bg-white/[0.04] hover:border-white/30 px-4 py-4 text-left flex items-center gap-3 transition-colors"
        >
          <div className="w-10 h-10 rounded-xl bg-white/[0.07] flex items-center justify-center shrink-0"><Newspaper size={18} className="text-white/70" /></div>
          <div className="flex-1">
            <div className="text-[13px] font-bold">Continuez sur le Kiosque AYA</div>
            <div className="text-[11px] text-white/50">Journaux, livres, mangas et revues vous attendent</div>
          </div>
          <ChevronRight size={16} className="text-white/40" />
        </button>
      </div>

      {selected && <ProductSheet product={selected} merchant={merchant} onClose={() => setSelected(null)} />}
      {flipping && <FlipViewer catalog={catalog} merchant={merchant} products={products} onClose={() => setFlipping(false)} onProduct={(p) => { setFlipping(false); setSelected(p); trackCatalog("catalog_product_click", { catalog_id: catalog.catalogId, product_id: p.product_id, src: "flip" }); }} />}
    </div>
  );
}
